import React, { useState } from 'react';
import { Alert, AlertActionLink, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import type { ApiResponseDnsRrsetDelete } from '../../../types/api/ApiResponseDomainName';
import type { ZoneRecord } from '../../../types/models/Zone';
import { displayRecordName } from './zoneRecords';

interface RecordSetDeleteModalProps {
  /** The RRSet to delete; the modal is open iff this is non-null (mirrors the mail delete modals). */
  record: ZoneRecord | null;
  domainId: number;
  zoneApex: string;
  onClose: () => void;
  onSuccess: () => void;
  /** Close this modal and open the edit modal for the same set — to remove a single value, not the whole set. */
  onEditInstead: () => void;
}

function RecordSetDeleteModal({
  record,
  domainId,
  zoneApex,
  onClose,
  onSuccess,
  onEditInstead,
}: RecordSetDeleteModalProps) {
  const apiFetch = useApiFetch();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isOpen = record !== null;

  const resetAndClose = () => {
    setError(null);
    setIsSubmitting(false);
    onClose();
  };

  // Switch to editing this set instead of deleting it (e.g. to drop a single value and keep the rest).
  // The parent closes this modal and opens the edit modal on the same record.
  const handleEditInstead = () => {
    if (isSubmitting) {
      return;
    }
    setError(null);
    onEditInstead();
  };

  const handleDelete = async () => {
    if (record === null) {
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      // name is the canonical FQDN — send record.name as-is (exactly like the toggle handlers); no toFqdn.
      const response = await apiFetch(`/datacenters/domain-names/${domainId}/dns/rrset`, {
        method: 'DELETE',
        headers: { Accept: 'application/json' },
        body: JSON.stringify({ name: record.name, type: record.type }),
      });

      let body: ApiResponseDnsRrsetDelete | null;
      try {
        body = (await response.json()) as ApiResponseDnsRrsetDelete;
      } catch {
        body = null;
      }

      // The DNS write endpoints always return HTTP 200 with a JSend body — branch on status, not ok.
      if (response.ok && body?.status === 'success' && body.data?.record_set) {
        onSuccess();
        resetAndClose();
        return;
      }
      setError(body?.message ?? `Request failed with status ${response.status}`.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const valueSummary =
    record !== null && record.content.length === 1 ? 'its 1 value' : `all ${record?.content.length ?? 0} values`;

  return (
    <Modal variant="small" isOpen={isOpen} onClose={resetAndClose} aria-label="Delete record set">
      <ModalHeader title="Delete record set" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {record !== null && (
          <>
            {record.content.length > 1 && (
              <Alert
                variant="info"
                isInline
                title="Removing just one value?"
                className="pf-v6-u-mb-md"
                actionLinks={
                  <AlertActionLink onClick={handleEditInstead} isDisabled={isSubmitting}>
                    Edit the record set instead
                  </AlertActionLink>
                }
              >
                Deleting here removes the entire {record.type} record set (all {record.content.length} values). To
                remove only one value and keep the rest, edit the record set instead.
              </Alert>
            )}
            <Content>
              Delete the <strong>{record.type}</strong> record set{' '}
              <strong>{displayRecordName(record.name, zoneApex, 'relative')}</strong>? This removes {valueSummary} from
              the live zone.
            </Content>
            {record.content.length > 0 && (
              <Content component="pre" className="pf-v6-u-mt-sm pf-v6-u-mb-sm">
                {record.content.join('\n')}
              </Content>
            )}
            <Content>This takes effect immediately and can&apos;t be undone here, but the set can be recreated.</Content>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleDelete()} isLoading={isSubmitting} isDisabled={isSubmitting}>
          Delete
        </Button>
        <Button variant="link" onClick={resetAndClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default RecordSetDeleteModal;
